import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-07 (the R8 graduation, mirrors
// study_companion / mood_tracker / oriora_keycap / oriora_gifted / oriora_hivewise /
// oriora_inkwell). askBrain sends the message + this app's declared action catalog to the generic
// fleet chat brain (POST /api/apps/oriora_knowcode/chat) — ONE billed call returns ONE registered
// action + a reply; the app executes it locally so indexed code stays on-device (only matched
// snippets ever leave, exactly as the existing Ask flow already works). The regex intent-router
// below is the FREE FALLBACK (offline / signed-out / out of funds) — chat never goes dead. Original
// prototype notes:
//   • "index a folder / add a repo"        → the SAME native folder picker as the in-app "Index a
//                                              folder" button → store.indexFolder (local, free)
//   • "list / show repos"                  → store.listRepos (local, free)
//   • "remove / delete repo <name>"        → resolve locally → delete_repo (UI confirms)
//   • "clear history" / "show history"     → store.clearHistory / store.listHistory (local, free)
//   • "where is X defined" / "find symbol X" → store.findSymbols — deterministic, on-device, NEVER
//                                              the LLM (mirrors the Symbol map's own contract)
//   • anything else that reads as a real question about the code → the EXISTING /ask endpoint
//                                              (askCodebase): local FTS5 retrieval first, then the
//                                              ONE billed BYOK call — exactly like AskBox does
//   • can't resolve a target repo / unclear → none + a short clarifying nudge
//
// CODE-EXPLAINER SCOPE FENCE (no client fence FUNCTION to import — the grounded-only, cite-
// everything contract is enforced entirely server-side in the /ask prompt, ASK_SYSTEM_PROMPT in
// ../brain.ts: "ANSWER ONLY FROM THE SNIPPETS… never assume code that isn't shown"). This app never
// claims to RUN or EXECUTE code — it reads, explains, and cites indexed snippets only. CHAT_ACTIONS
// below deliberately has NO "run"/"execute" action (fence-by-catalog-omission, mirrors actions.ts's
// ChatAction union, which has no such kind) — symbol lookup stays fully deterministic (NEVER the
// LLM), and "ask" is a SECOND billed call that reuses the existing local-retrieval-then-BYOK-call
// shape, never sending whole files, only the matched snippets.

import { store, type Repo } from "../store";
import { BrainError, brainCall } from "../brain";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, RepoRow, SymbolRow, HistoryRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  // assistant turns may carry rows to render inline
  repoRows?: RepoRow[];
  symbolRows?: SymbolRow[];
  historyRows?: HistoryRow[];
}
export interface BrainResponse {
  reply: string;         // conversational preface (grounded-only code Q&A; no domain fence needed)
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

const HELP =
  "I can index a folder, list or remove a repo, look up a symbol, browse/clear your question history, " +
  "or answer a question about your indexed code — just tell me. Try:\n" +
  '• "index a folder"\n' +
  '• "list repos"\n' +
  '• "where is login defined"\n' +
  '• "how does auth work"\n' +
  '• "show history" · "remove repo my-app"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

async function findRepoByName(term: string): Promise<Repo[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listRepos();
  const subs = all.filter((r) => r.name.toLowerCase().includes(q));
  if (subs.length) return subs;
  // Fleet FUZZY standard (fuzzy.ts): forgiving near-match so a mistyped/paraphrased repo resolves.
  return findNamedMatches(all, term); // Repo has `name`
}
/** "Couldn't find X" enriched with the closest repo names ("did you mean …?"). */
async function repoNotFound(term: string): Promise<string> {
  const all = await store.listRepos();
  return notFoundMessage(all, term);
}

function pickRepoList(rows: Repo[]): string {
  return rows.slice(0, 5).map((r) => `• ${r.name}`).join("\n");
}

// Resolve WHICH repo a question / symbol-lookup targets: an explicit name hint first (from the
// server's `repo` param, or a text "in <name>" mention), else the currently active repo (passed in
// from Home), else — if exactly one is indexed — that one.
async function resolveTargetRepo(
  explicitName: string | undefined,
  text: string,
  activeRepoId: string | null,
): Promise<{ repo: Repo | null; error?: string }> {
  const all = await store.listRepos();
  if (all.length === 0) return { repo: null, error: 'No repo indexed yet — try "index a folder" first.' };

  const hintedName = explicitName ?? text.match(/\bin\s+(?:the\s+)?["']?([\w.\-/]+)["']?\s*(?:repo)?\s*$/i)?.[1];
  if (hintedName) {
    const hit = all.find((r) => r.name.toLowerCase() === hintedName.toLowerCase()) ?? all.find((r) => r.name.toLowerCase().includes(hintedName.toLowerCase()));
    if (hit) return { repo: hit };
  }

  if (activeRepoId) {
    const hit = all.find((r) => r.id === activeRepoId);
    if (hit) return { repo: hit };
  }

  if (all.length === 1) return { repo: all[0] };
  return { repo: null, error: `I need to know which repo — you have: ${all.map((r) => r.name).join(", ")}. Try "…in ${all[0].name}".` };
}

export async function askBrain(history: ChatTurn[], activeRepoId: string | null, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text, activeRepoId); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, activeRepoId, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text, activeRepoId);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (repo name text → id) locally,
// because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.knowcode.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "index_repo", description: "Open the native folder picker to index a new project folder. No params.", params: {} },
  { name: "list_repos", description: "List the indexed repos. No params.", params: {} },
  { name: "delete_repo", description: "Remove one repo's index (source files on disk are untouched; the app asks the user to confirm first).", params: { target: "REQUIRED — which repo, name as the user wrote it" } },
  { name: "find_symbol", description: "Find WHERE a function/class/variable is defined — deterministic, on-device lookup, never the AI.", params: { query: "REQUIRED — the symbol name to find", repo: "optional — which repo, name as the user wrote it; omit to use the active or only repo" } },
  { name: "ask", description: "Answer a real question about the indexed code, grounded ONLY in the matched snippets and cited by file:line. Never claims to run or execute code — reads and explains only.", params: { question: "REQUIRED — the user's question, verbatim", repo: "optional — which repo, name as the user wrote it; omit to use the active or only repo" } },
  { name: "show_history", description: "Show recent asked questions. No params.", params: {} },
  { name: "clear_history", description: "Clear the question history (the app asks the user to confirm first). No params.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole index — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, activeRepoId: string | null, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const repos = await store.listRepos();
  const names = repos.slice(0, 40).map((r) => r.name).join(", ");
  const active = activeRepoId ? repos.find((r) => r.id === activeRepoId)?.name : undefined;
  const _notes = getInstructions();
  const context =
    `LIBRARY (their saved plans/notes): ${librarySummary()}. Indexed repos: ${names || "(none yet)"}.` +
    (active ? ` Currently active repo: ${active}.` : "") +
    (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);

  // The shared brain returns an ARRAY (G17): "find symbol X and explain how auth works" = two actions.
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text, activeRepoId);
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);

export async function toAction(r: ServerChat, rawText: string, activeRepoId: string | null): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "index_repo":
      return { reply: say, action: { kind: "index_repo" } };
    case "list_repos":
      return { reply: say, action: { kind: "list_repos" } };
    case "delete_repo": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which repo do you want to remove?");
      const rows = await findRepoByName(term);
      if (rows.length === 0) return none(await repoNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickRepoList(rows)}`);
      return { reply: say, action: { kind: "delete_repo", id: rows[0].id, label: rows[0].name } };
    }
    case "find_symbol": {
      const query = asStr(p.query);
      if (!query) return none(say || 'What symbol should I find? Try "where is login defined".');
      const { repo, error } = await resolveTargetRepo(asStr(p.repo), rawText, activeRepoId);
      if (!repo) return none(error ?? HELP);
      return { reply: say, action: { kind: "find_symbol", repoId: repo.id, repoName: repo.name, query } };
    }
    case "ask": {
      const question = asStr(p.question) ?? rawText;
      const { repo, error } = await resolveTargetRepo(asStr(p.repo), rawText, activeRepoId);
      if (!repo) return none(error ?? HELP);
      return { reply: say, action: { kind: "ask", repoId: repo.id, repoName: repo.name, question } };
    }
    case "show_history":
      return { reply: say, action: { kind: "show_history" } };
    case "clear_history":
      return { reply: say, action: { kind: "clear_history" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string, activeRepoId: string | null): Promise<One> {
  const t = text.trim();
  if (!t) return none(HELP);
  const low = t.toLowerCase();

  // 1) INDEX A FOLDER — the SAME native picker as the in-app button (local, free).
  if (/^index\b/i.test(low) || /^add\s+(?:a\s+|another\s+)?(?:folder|repo|project|codebase)\b/i.test(low)) {
    return { reply: "", action: { kind: "index_repo" } };
  }

  // 2) LIST REPOS.
  if (/\b(list|show|which)\b.*\brepos?\b/i.test(low) || /^repos?\s*\??$/i.test(low)) {
    return { reply: "", action: { kind: "list_repos" } };
  }

  // 3) HISTORY — clear (destructive → UI confirms) / show.
  if (/\b(clear|delete|remove)\b.*\bhistor(y|ies)\b/i.test(low)) {
    return { reply: "", action: { kind: "clear_history" } };
  }
  if (/\b(show|recent|list|view)\b.*\bhistor(y|ies)\b/i.test(low) || /^histor(y|ies)\s*\??$/i.test(low)) {
    return { reply: "", action: { kind: "show_history" } };
  }

  // 4) REMOVE / DELETE a repo — resolve locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:repo\s+|index\s+(?:for|of)\s+)?(.+)$/i);
  if (delM) {
    const term = delM[1].trim();
    const rows = await findRepoByName(term);
    if (rows.length === 0) return none(await repoNotFound(term));
    if (rows.length > 1) {
      return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickRepoList(rows)}`);
    }
    return { reply: "", action: { kind: "delete_repo", id: rows[0].id, label: rows[0].name } };
  }

  // 5) SYMBOL LOOKUP — deterministic, on-device, NEVER the LLM.
  const symM = t.match(/^(?:where\s+is)\s+(.+?)\s+defined\b/i) || t.match(/^(?:find|locate)\s+(?:the\s+)?symbol\s+(.+)$/i);
  if (symM) {
    const query = symM[1].trim();
    const { repo, error } = await resolveTargetRepo(undefined, t, activeRepoId);
    if (!repo) return none(error ?? HELP);
    return { reply: "", action: { kind: "find_symbol", repoId: repo.id, repoName: repo.name, query } };
  }

  // 6) ASK — everything else that reads as a real question about the code → the EXISTING /ask
  // endpoint (local retrieval + the one billed BYOK call), exactly like AskBox.
  if (/[?]|^(where|how|what|why|explain|describe|show\s+me)\b/i.test(low)) {
    const { repo, error } = await resolveTargetRepo(undefined, t, activeRepoId);
    if (!repo) return none(error ?? HELP);
    return { reply: "", action: { kind: "ask", repoId: repo.id, repoName: repo.name, question: t } };
  }

  // 7) Unclear → a short clarifying nudge.
  return none(HELP);
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
